import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO, { faqSchema } from '../../Components/SEO';
import SEOPageLayout from '../../Components/SEOPageLayout';

export default function FAQ() {
  const [openFaq, setOpenFaq] = useState(null);
  
  const allFaqs = [
    // Getting Started
    { category: "Getting Started", question: "What is CheckScript AI?", answer: "CheckScript AI is an advanced Windows application that acts as your personal AI assessment assistant. It captures your screen, analyzes questions or code using OCR and Vision models, and provides instant, accurate solutions via customizable hotkeys." },
    { category: "Getting Started", question: "What are the system requirements?", answer: "CheckScript requires Windows 10 or Windows 11 (64-bit). The core app is lightweight and runs on 4GB RAM. If you plan to use the offline Neural Driver AI feature, you will need a dedicated NVIDIA GPU with at least 6GB of VRAM." },
    { category: "Getting Started", question: "How do I install the software?", answer: "Simply download the executable from our downloads page, run it as an administrator, and follow the on-screen prompts. CheckScript runs silently in your system tray." },
    
    // Features
    { category: "Features", question: "What is the Advanced stealth capture system capture?", answer: "Advanced stealth capture system is our proprietary Advanced stealth capture system screen capture technology. It utilizes Hardware-accelerated capture, Magnification API, Hybrid Compositor, PrintWindow, and GDI to ensure it can capture content from any application, even secure testing environments that block standard screenshots." },
    { category: "Features", question: "How does the Neural Driver work?", answer: "Neural Driver allows you to run Vision-Language AI models (like Qwen2-VL) directly on your local GPU. This provides 100% offline functionality, zero API latency, and complete data privacy. It includes a built-in ChatGPT-style web interface accessible at Private local connection." },
    { category: "Features", question: "What is CheckScript Remote?", answer: "CheckScript Remote is a Ultra-low latency streaming protocol-based remote desktop solution built into the host. It allows someone to view and control your computer remotely directly from a standard web browser, with ultra-low latency (<100ms) and H.264 video quality." },
    
    // Pricing
    { category: "Pricing", question: "How much does CheckScript cost?", answer: "The software itself is free to download. We operate on a pay-per-question model. Questions cost ₹12.59 each, dropping to ₹10 each for bulk purchases. The minimum purchase is 25 questions." },
    { category: "Pricing", question: "Are there premium features?", answer: "Yes, for a one-time upgrade of ₹199, you unlock Premium features including the Auto-Typing engine (which types code for you to bypass paste restrictions), advanced process hiding, and enterprise firewall bypass capabilities." },
    { category: "Pricing", question: "How do I pay?", answer: "We support instant UPI payments. When you generate an invoice in the app, it will display a QR code that you can scan with Google Pay, PhonePe, Paytm, or any standard UPI app." },
    
    // Technical
    { category: "Technical", question: "Is it safe to use?", answer: "Yes, CheckScript is completely safe and free of malware. It is designed as an educational and productivity tool. Please use it responsibly and in accordance with your institution's guidelines." },
    { category: "Technical", question: "Why does it trigger my antivirus?", answer: "Because CheckScript utilizes low-level Windows APIs for screen capture and keystroke injection (to provide its advanced features), some over-aggressive antivirus software may flag it as a false positive. You may need to add an exclusion for the application." },
    
    // Support
    { category: "Support", question: "Do you offer a partner program?", answer: "Yes, we offer a highly lucrative vendor/partner program for bulk buyers and resellers, offering up to 200% ROI. You can find more details on our Partners page." },
    { category: "Support", question: "How can I get technical support?", answer: "You can reach out to us via our Contact page. We also offer paid, 1-on-1 remote technical support sessions if you need personalized help setting up the software or the Neural Driver." }
  ];

  return (
    <SEOPageLayout>
      <SEO
        title="FAQ — CheckScript AI Assessment Assistant | Common Questions Answered"
        description="Find answers to all your questions about CheckScript AI. Learn about installation, Neural Driver, Advanced stealth capture system capture, pricing, and system requirements."
        keywords="CheckScript FAQ, AI assessment tool questions, how does CheckScript work, CheckScript help"
        path="/faq"
        structuredData={faqSchema(allFaqs)}
      />

      <section className="seo-hero seo-animate">
        <div className="seo-mesh-bg"></div>
        <div className="seo-container seo-container--sm">
          <div className="seo-hero-badge">❓ Help Center</div>
          <h1 className="seo-h1">
            Frequently Asked <span className="seo-gradient-text">Questions</span>
          </h1>
          <p className="seo-lead">
            Everything you need to know about CheckScript AI, our pricing, and how to maximize your study workflow.
          </p>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container seo-container--sm">
          
          <h2 className="seo-h2 seo-mb-32">Getting Started</h2>
          <ul className="seo-faq-list seo-mb-64">
            {allFaqs.filter(f => f.category === 'Getting Started').map((faq, i) => (
              <li key={`gs-${i}`} className={`seo-faq-item ${openFaq === `gs-${i}` ? 'active' : ''}`}>
                <button className="seo-faq-question" onClick={() => setOpenFaq(openFaq === `gs-${i}` ? null : `gs-${i}`)}>
                  {faq.question}
                  <span className="seo-faq-arrow">▼</span>
                </button>
                <div className="seo-faq-answer">
                  <div className="seo-faq-answer-inner">{faq.answer}</div>
                </div>
              </li>
            ))}
          </ul>

          <h2 className="seo-h2 seo-mb-32">Features & Technology</h2>
          <ul className="seo-faq-list seo-mb-64">
            {allFaqs.filter(f => f.category === 'Features').map((faq, i) => (
              <li key={`feat-${i}`} className={`seo-faq-item ${openFaq === `feat-${i}` ? 'active' : ''}`}>
                <button className="seo-faq-question" onClick={() => setOpenFaq(openFaq === `feat-${i}` ? null : `feat-${i}`)}>
                  {faq.question}
                  <span className="seo-faq-arrow">▼</span>
                </button>
                <div className="seo-faq-answer">
                  <div className="seo-faq-answer-inner">{faq.answer}</div>
                </div>
              </li>
            ))}
          </ul>

          <h2 className="seo-h2 seo-mb-32">Pricing & Payments</h2>
          <ul className="seo-faq-list seo-mb-64">
            {allFaqs.filter(f => f.category === 'Pricing').map((faq, i) => (
              <li key={`price-${i}`} className={`seo-faq-item ${openFaq === `price-${i}` ? 'active' : ''}`}>
                <button className="seo-faq-question" onClick={() => setOpenFaq(openFaq === `price-${i}` ? null : `price-${i}`)}>
                  {faq.question}
                  <span className="seo-faq-arrow">▼</span>
                </button>
                <div className="seo-faq-answer">
                  <div className="seo-faq-answer-inner">{faq.answer}</div>
                </div>
              </li>
            ))}
          </ul>

          <h2 className="seo-h2 seo-mb-32">Technical & Support</h2>
          <ul className="seo-faq-list">
            {allFaqs.filter(f => f.category === 'Technical' || f.category === 'Support').map((faq, i) => (
              <li key={`tech-${i}`} className={`seo-faq-item ${openFaq === `tech-${i}` ? 'active' : ''}`}>
                <button className="seo-faq-question" onClick={() => setOpenFaq(openFaq === `tech-${i}` ? null : `tech-${i}`)}>
                  {faq.question}
                  <span className="seo-faq-arrow">▼</span>
                </button>
                <div className="seo-faq-answer">
                  <div className="seo-faq-answer-inner">{faq.answer}</div>
                </div>
              </li>
            ))}
          </ul>

        </div>
      </section>

      <section className="seo-cta seo-animate">
        <div className="seo-container">
          <div className="seo-cta-box">
            <h2 className="seo-h2">Still have questions?</h2>
            <p className="seo-text-secondary seo-mb-32">Our support team is ready to help you get the most out of CheckScript.</p>
            <Link to="/contact" className="seo-btn seo-btn--primary">Contact Support</Link>
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
}
